import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { readingBeeAPI, recordingsAPI } from '@/lib/api';
import { Trophy, Play, Pause, Star, BookOpen, Users, Clock, Award, Mic } from 'lucide-react';
import { toast } from 'sonner';

const C = {
  bg: '#0A0F1E', card: '#1A2236', surface: '#111827',
  gold: '#D4A853', goldLight: '#F5D799', teal: '#38BDF8',
  cream: '#F8F5EE', muted: '#94A3B8',
};

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const StudentReadingBee = ({ studentId, studentName, onOpenStory }) => {
  const queryClient = useQueryClient();
  const [expandedBee, setExpandedBee] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [audioEl, setAudioEl] = useState(null);

  const { data: bees = [], isLoading } = useQuery({
    queryKey: ['student-reading-bees'],
    queryFn: async () => {
      try {
        const res = await readingBeeAPI.getAll();
        return res.data || [];
      } catch { return []; }
    },
  });

  const { data: beeDetail } = useQuery({
    queryKey: ['student-reading-bee-detail', expandedBee],
    queryFn: async () => (await readingBeeAPI.getById(expandedBee)).data,
    enabled: !!expandedBee,
  });

  const { data: myRecordings } = useQuery({
    queryKey: ['student-recordings-for-bee', studentId],
    queryFn: async () => (await recordingsAPI.getForStudent(studentId)).data,
    enabled: !!studentId && !!expandedBee,
  });

  const submitMut = useMutation({
    mutationFn: ({ beeId, data }) => readingBeeAPI.submitEntry(beeId, data),
    onSuccess: () => { queryClient.invalidateQueries(['student-reading-bee-detail']); queryClient.invalidateQueries(['student-reading-bees']); toast.success('Entry submitted!'); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to submit entry'),
  });

  const voteMut = useMutation({
    mutationFn: ({ beeId, data }) => readingBeeAPI.vote(beeId, data),
    onSuccess: () => { queryClient.invalidateQueries(['student-reading-bee-detail']); toast.success('Vote recorded!'); },
    onError: (err) => toast.error(err.response?.data?.detail || 'Already voted'),
  });

  const playEntry = async (recordingId) => {
    if (playing === recordingId && audioEl) { audioEl.pause(); setPlaying(null); return; }
    if (audioEl) audioEl.pause();
    let url;
    try { url = await recordingsAPI.streamUrl(recordingId); } catch { toast.error('Recording not available'); return; }
    const a = new Audio(url);
    a.onended = () => setPlaying(null);
    a.onerror = () => { setPlaying(null); toast.error('Recording not available'); };
    a.play().catch(() => setPlaying(null));
    setAudioEl(a);
    setPlaying(recordingId);
  };

  const getMatchingRecordings = (narrativeId) => {
    if (!myRecordings?.recordings) return [];
    return myRecordings.recordings.filter(r => r.narrative_id === narrativeId);
  };

  if (isLoading) return <p className="text-center py-8 font-bold" style={{ color: C.muted }}>Loading Reading Bees...</p>;

  const activeBees = bees.filter(b => b.status !== 'closed');
  const closedBees = bees.filter(b => b.status === 'closed');

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(212,168,83,0.15)' }}>
          <Trophy size={24} style={{ color: C.gold }} />
        </div>
        <div>
          <h2 className="text-xl font-bold" style={{ color: C.cream }}>Reading Bee Contests</h2>
          <p className="text-xs" style={{ color: C.muted }}>Read expressively, record yourself, and compete!</p>
        </div>
      </div>

      {bees.length === 0 ? (
        <div className="text-center py-12 rounded-2xl" style={{ background: C.card, border: '1px solid rgba(255,255,255,0.08)' }}>
          <Trophy size={40} className="mx-auto mb-3" style={{ color: C.muted }} />
          <p className="text-lg font-bold" style={{ color: C.cream }}>No Active Reading Bees</p>
          <p className="text-sm" style={{ color: C.muted }}>Check back soon for new contests!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Active Bees */}
          {activeBees.map(bee => (
            <div key={bee.id} className="rounded-2xl overflow-hidden" style={{ background: C.card, border: `1px solid ${expandedBee === bee.id ? 'rgba(212,168,83,0.4)' : 'rgba(255,255,255,0.08)'}` }}>
              {/* Header */}
              <div className="p-4 cursor-pointer" onClick={() => setExpandedBee(expandedBee === bee.id ? null : bee.id)}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Trophy size={16} style={{ color: C.gold }} />
                      <h3 className="text-base font-bold" style={{ color: C.cream }}>{bee.title}</h3>
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full"
                        style={{ background: bee.status === 'open' ? 'rgba(52,211,153,0.15)' : 'rgba(251,191,36,0.15)', color: bee.status === 'open' ? '#34D399' : '#FBBF24' }}>
                        {bee.status === 'open' ? 'Accepting Entries' : 'Voting Open'}
                      </span>
                    </div>
                    <p className="text-xs" style={{ color: C.muted }}>
                      <BookOpen size={10} className="inline mr-1" />{bee.narrative_title}
                      {' · '}<Users size={10} className="inline mr-1" />{bee.entry_count} entries
                    </p>
                    {bee.prize_description && (
                      <p className="text-xs mt-1" style={{ color: C.gold }}>
                        <Award size={10} className="inline mr-1" />Prize: {bee.prize_description}
                      </p>
                    )}
                  </div>
                  {bee.has_entered && (
                    <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-lg" style={{ background: 'rgba(99,102,241,0.15)', color: '#818CF8' }}>Entered</span>
                  )}
                </div>
              </div>

              {/* Expanded */}
              {expandedBee === bee.id && beeDetail && (
                <div className="px-4 pb-4 space-y-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  {beeDetail.description && <p className="text-sm italic pt-3" style={{ color: C.muted }}>{beeDetail.description}</p>}

                  {/* Winner celebration when bee is closed. If the current
                      student IS the winner, show a big confetti banner. */}
                  {beeDetail.status === 'closed' && beeDetail.winner_entry_id && (() => {
                    const winner = (beeDetail.entries || []).find((e) => e.id === beeDetail.winner_entry_id);
                    if (!winner) return null;
                    const iAmWinner = winner.submitted_by === studentId || winner.student_id === studentId;
                    return (
                      <div
                        className="p-5 rounded-2xl text-center"
                        style={{
                          background: iAmWinner
                            ? 'linear-gradient(135deg, rgba(212,168,83,0.25), rgba(245,215,153,0.15))'
                            : 'rgba(212,168,83,0.08)',
                          border: `2px solid ${C.gold}`,
                        }}
                      >
                        <div className="text-4xl mb-2">{iAmWinner ? '🎉🏆🎉' : '🏆'}</div>
                        <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: C.goldLight }}>
                          {iAmWinner ? 'You won!' : 'Winner announced'}
                        </p>
                        <p className="text-2xl font-bold mb-2" style={{ color: C.cream }}>
                          {winner.student_name}
                        </p>
                        <p className="text-xs mb-4" style={{ color: C.muted }}>
                          Ch. {winner.chapter_number} · avg score {winner.average_score || '—'}
                          {winner.vote_count ? ` · ${winner.vote_count} votes` : ''}
                        </p>
                        <button
                          onClick={(e) => { e.stopPropagation(); playEntry(winner.recording_id); }}
                          className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold text-black"
                          style={{ background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` }}
                        >
                          {playing === winner.recording_id ? <Pause size={14} /> : <Play size={14} />}
                          Listen to the Winning Read
                        </button>
                      </div>
                    );
                  })()}

                  {/* Submit Entry */}
                  {beeDetail.status === 'open' && !beeDetail.has_entered && (
                    <div className="p-4 rounded-xl" style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)' }}>
                      <h4 className="text-xs font-bold uppercase mb-2" style={{ color: '#34D399' }}>
                        <Mic size={12} className="inline mr-1" />Submit Your Recording
                      </h4>
                      <p className="text-xs mb-3" style={{ color: C.muted }}>
                        Record yourself reading "{beeDetail.narrative_title}" then select it below:
                      </p>
                      {getMatchingRecordings(beeDetail.narrative_id).length > 0 ? (
                        <div className="space-y-2">
                          {getMatchingRecordings(beeDetail.narrative_id).map(rec => (
                            <div key={rec.id} className="flex items-center justify-between p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                              <div className="flex items-center gap-3">
                                <button onClick={(e) => { e.stopPropagation(); playEntry(rec.id); }}
                                  className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: '#818CF8' }}>
                                  {playing === rec.id ? <Pause size={12} className="text-white" /> : <Play size={12} className="text-white" />}
                                </button>
                                <div>
                                  <p className="text-sm font-bold" style={{ color: C.cream }}>Ch. {rec.chapter_number}</p>
                                  <p className="text-[11px]" style={{ color: C.muted }}>{new Date(rec.created_date).toLocaleDateString()}</p>
                                </div>
                              </div>
                              <button onClick={() => submitMut.mutate({ beeId: beeDetail.id, data: { recording_id: rec.id, student_name: studentName || rec.student_name, student_age: rec.student_age } })}
                                className="px-4 py-2 rounded-xl text-xs font-bold text-black"
                                style={{ background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` }}>
                                Enter Contest
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 rounded-xl text-center" style={{ background: 'rgba(212,168,83,0.08)', border: '1px dashed rgba(212,168,83,0.4)' }}>
                          <p className="text-sm font-bold mb-1" style={{ color: C.cream }}>
                            You need to record yourself first!
                          </p>
                          <p className="text-xs mb-3" style={{ color: C.muted }}>
                            Open the story "<span style={{ color: C.gold }}>{beeDetail.narrative_title}</span>" and tap the 🎤 <b>Read Aloud</b> button while reading a chapter.
                          </p>
                          {onOpenStory && beeDetail.id && (
                            <button
                              onClick={() => onOpenStory(beeDetail.id)}
                              className="px-5 py-3 rounded-xl text-sm font-bold text-black inline-flex items-center gap-2"
                              style={{ background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` }}
                            >
                              <Mic size={16} /> Open Story & Record Now
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {beeDetail.has_entered && beeDetail.status === 'open' && (
                    <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)' }}>
                      <p className="text-sm font-bold" style={{ color: '#818CF8' }}>You've entered this contest! Good luck!</p>
                    </div>
                  )}

                  {/* Entries + Voting */}
                  {beeDetail.entries?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold uppercase mb-2" style={{ color: C.muted }}>
                        Entries ({beeDetail.entries.length})
                      </h4>
                      <div className="space-y-2">
                        {beeDetail.entries.map((entry, idx) => (
                          <div key={entry.id} className="flex items-center justify-between p-3 rounded-xl"
                            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                            <div className="flex items-center gap-3">
                              <span className="text-lg font-black w-6" style={{ color: 'rgba(255,255,255,0.15)' }}>#{idx + 1}</span>
                              <button onClick={() => playEntry(entry.recording_id)}
                                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#818CF8' }}>
                                {playing === entry.recording_id ? <Pause size={12} className="text-white" /> : <Play size={12} className="text-white" />}
                              </button>
                              <div>
                                <p className="text-sm font-bold" style={{ color: C.cream }}>{entry.student_name}{entry.student_age ? ` (age ${entry.student_age})` : ''}</p>
                                <p className="text-[11px]" style={{ color: C.muted }}>
                                  {entry.vote_count} votes · Avg: {entry.average_score || '—'}
                                </p>
                              </div>
                            </div>

                            {/* Vote */}
                            {(beeDetail.status === 'open' || beeDetail.status === 'voting') && (
                              <div className="flex items-center gap-0.5">
                                {[1,2,3,4,5].map(s => (
                                  <button key={s} onClick={() => voteMut.mutate({ beeId: beeDetail.id, data: { entry_id: entry.id, score: s } })} className="p-0.5">
                                    <Star size={18} className={s <= (beeDetail.user_votes?.[entry.id] || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-600 hover:text-amber-300'} />
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Closed Bees — Results */}
          {closedBees.length > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase mt-6 mb-2" style={{ color: C.muted }}>Past Contests</h3>
              {closedBees.map(bee => (
                <div key={bee.id} className="p-4 rounded-2xl mb-2" style={{ background: C.card, border: '1px solid rgba(255,255,255,0.06)', opacity: 0.7 }}>
                  <div className="flex items-center gap-2">
                    <Trophy size={14} style={{ color: C.muted }} />
                    <p className="text-sm font-bold" style={{ color: C.cream }}>{bee.title}</p>
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}>Closed</span>
                  </div>
                  <p className="text-xs mt-1" style={{ color: C.muted }}>{bee.narrative_title} · {bee.entry_count} entries</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentReadingBee;
